import type { CeoBankAccount, CeoBankAccountsSummary } from '@shared/api';
import {
  ACCOUNT_TYPE_LABELS,
  formatDateTimeRu,
  formatMoney,
} from '../ceoFormat';
import { BankAccountHead, HeaderActions, WrapButton } from '../ui';
import {
  AccFooter,
  AccNumber,
  AccountCardBox,
  AccountsGrid,
  BalanceBig,
  BlockedAmountText,
  Section,
  SectionHeader,
  SectionLink,
  SectionSubtext,
  SectionTitle,
  SubRow,
} from './styles';

interface DashboardAccountsSectionProps {
  accounts: CeoBankAccount[];
  accSum: CeoBankAccountsSummary;
  can_write: boolean;
  isSyncing: boolean;
  onSync: () => void;
}

export function DashboardAccountsSection({
  accounts,
  accSum,
  can_write,
  isSyncing,
  onSync,
}: DashboardAccountsSectionProps) {
  return (
    <Section>
      <SectionHeader>
        <div>
          <SectionTitle>
            Остатки по банковским счетам (Локальный снимок БД / Выписки 1С)
          </SectionTitle>
          <SectionSubtext>
            Всего на счетах: <strong>{formatMoney(accSum.total_current_balance)}</strong> ·
            Свободный операционный остаток (с овердрафтом):{' '}
            <strong>{formatMoney(accSum.free_operational_cash)}</strong> · Спецсчета ОБС:{' '}
            <strong>{formatMoney(accSum.special_obs_balance)}</strong> · Остаток на счёте
            налогового резерва:{' '}
            <strong>{formatMoney(accSum.tax_reserve_balance)}</strong>
          </SectionSubtext>
        </div>
        <HeaderActions>
          {can_write && (
            <WrapButton
              $size="sm"
              $variant="secondary"
              disabled={isSyncing}
              onClick={onSync}
            >
              {isSyncing ? 'Обновление…' : 'Обновить счета'}
            </WrapButton>
          )}
          <SectionLink to="/agents/ceo/accounts">Все счета и выписки →</SectionLink>
        </HeaderActions>
      </SectionHeader>

      <AccountsGrid>
        {accounts.map((acc) => (
          <AccountCardBox key={acc.id}>
            <BankAccountHead account={acc} />
            <AccNumber>
              Р/с {acc.account_number} · {ACCOUNT_TYPE_LABELS[acc.account_type] ?? acc.account_type}
            </AccNumber>
            <BalanceBig>{formatMoney(acc.current_balance)}</BalanceBig>
            <SubRow>
              <span>Доступно к платежам:</span>
              <strong>{formatMoney(acc.available_balance)}</strong>
            </SubRow>
            {acc.blocked_amount > 0 && (
              <SubRow>
                <span>Целевая бронь / резерв:</span>
                <BlockedAmountText>{formatMoney(acc.blocked_amount)}</BlockedAmountText>
              </SubRow>
            )}
            {acc.overdraft_limit > 0 && (
              <SubRow>
                <span>Лимит овердрафта:</span>
                <span>{formatMoney(acc.overdraft_limit)}</span>
              </SubRow>
            )}
            <AccFooter>
              {acc.purpose_label} · Обновлено: {formatDateTimeRu(acc.api_last_sync_at)}
            </AccFooter>
          </AccountCardBox>
        ))}
      </AccountsGrid>
    </Section>
  );
}
